import { useEffect, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { usePeriodReportSnapshots } from './usePeriodReportSnapshots'
import { getReportSnapshotIds } from '../../utils/getReportSnapshotIds'
import { useProjektprognosEditingContext } from '../../context/ProjektprognosEditingContext'
import { useProjektprognosSheetContext } from '../../context/ProjektprognosSheetContext'
import useProjectId from '../../customHooks/useProjectId'

export const usePeriodReportSelectionEffect = (reportId: string | null) => {
  const queryClient = useQueryClient()
  const projectId = useProjectId()
  const { data: periodReports } = usePeriodReportSnapshots()
  const {
    setSelectedPeriodReport,
    setSelectedSnapshotId,
    setSnapshotMode,
    setReadOnlyMode,
  } = useProjektprognosEditingContext()
  const {
    setSelectedCashflowAccountIds,
    setSelectedPerformanceAccountIds,
    setMinDiff,
    setEnableDiffFilter,
    setEnableCommentFilter,
  } = useProjektprognosSheetContext()

  // Find selected report
  const selectedReport = useMemo(() => {
    if (!reportId || !periodReports) return null
    return periodReports.find((report) => report.id === reportId) || null
  }, [reportId, periodReports])

  // Get snapshot IDs
  const { currentSnapshotId } = useMemo(
    () => getReportSnapshotIds(selectedReport),
    [selectedReport]
  )

  useEffect(() => {
    if (!reportId) {
      // Clear state if no report is selected
      setSelectedPeriodReport(null)
      setSelectedSnapshotId('aktuell projektekonomi')
      setSnapshotMode(false)
      setReadOnlyMode(false)
      return
    }

    if (!selectedReport) return

    // Invalidate snapshot cache when a new report is selected
    queryClient.invalidateQueries({
      queryKey: ['periodReportPreviousSnapshot', projectId],
    })
    queryClient.invalidateQueries({
      queryKey: ['periodReportCurrentSnapshot', projectId],
    })

    // Update selected report
    setSelectedPeriodReport(selectedReport)

    // Activate snapshot mode and read-only
    setSnapshotMode(true)
    setReadOnlyMode(true)

    // Apply filter settings from period report
    if (selectedReport.filter) {
      setSelectedCashflowAccountIds(
        new Set(selectedReport.filter.cashFlowAccountIds)
      )
      setSelectedPerformanceAccountIds(
        new Set(selectedReport.filter.performanceAccountIds)
      )

      // Set minDiff from period report, default to 500 if not specified
      setMinDiff(selectedReport.filter.minDiff ?? 500)

      // Set enableDiffFilter - default to true (enabled) if not specified in period report
      setEnableDiffFilter(selectedReport.filter.enableDiffFilter ?? true)

      // Set enableCommentFilter - default to true if not specified in period report
      setEnableCommentFilter(selectedReport.filter.enableCommentFilter ?? true)
    } else {
      // No filter in period report - reset to defaults
      setMinDiff(500)
      setEnableDiffFilter(true)
      setEnableCommentFilter(true)
    }

    // Set selected snapshot ID to current snapshot for compatibility
    if (currentSnapshotId) {
      setSelectedSnapshotId(currentSnapshotId)
    }
  }, [
    reportId,
    selectedReport,
    currentSnapshotId,
    projectId,
    queryClient,
    setSelectedPeriodReport,
    setSelectedSnapshotId,
    setSnapshotMode,
    setReadOnlyMode,
    setSelectedCashflowAccountIds,
    setSelectedPerformanceAccountIds,
    setMinDiff,
    setEnableDiffFilter,
    setEnableCommentFilter,
  ])
}
